import dayjs from "dayjs";

/**
 * Event time phase (like task dashboard: closed / upcoming / beyond 30 days).
 * Uses end_datetime: past or status Closed → closed; end in next 30 days → upcoming; else → remaining (display: "Beyond 30 Days").
 */
export function getEventTimePhase(event, today, oneMonthLater) {
  const status = (event.status || "").toLowerCase();
  const isClosed = status === "closed";
  const end = dayjs(event.end_datetime);
  if (!end.isValid()) return "remaining";
  if (isClosed || end.isBefore(today)) return "closed";
  const t = end.valueOf();
  if (t >= today.valueOf() && t <= oneMonthLater.valueOf()) return "upcoming";
  return "remaining";
}

/**
 * Registration status from signed participant count vs max participants.
 * Returns: 'low_registration' | 'below_capacity' | 'almost_full' | 'full' | 'waitlist_active' | null (no cap).
 */
export function getRegistrationStatusKey(event) {
  const waitlisted = event.waitlisted_count ?? 0;
  if (waitlisted > 0) return "waitlist_active";

  const max = event.max_capacity;
  if (max == null || max <= 0) return null;
  const totalParticipants = event.participant_count ?? 0;
  const cancelled = event.cancelled_count ?? 0;
  const effectiveCount = Math.max(0, totalParticipants - cancelled);
  const pct = (effectiveCount / max) * 100;
  if (pct < 40) return "low_registration";
  if (pct < 80) return "below_capacity";
  if (pct < 100) return "almost_full";
  return "full";
}

/** Labels and colors for registration status — sequential teal palette (light to dark). */
export const REGISTRATION_STATUS_CONFIG = {
  low_registration: { label: "Low Registration", color: "#ccfbf1" },   // Teal 100 (lightest)
  below_capacity:   { label: "Below Capacity",   color: "#99f6e4" },   // Teal 200
  almost_full:      { label: "Almost Full",      color: "#5eead4" },   // Teal 300
  full:             { label: "Full",             color: "#2dd4bf" },   // Teal 400
  waitlist_active:  { label: "Waitlist Active", color: "#14b8a6" },   // Teal 500
  no_cap:           { label: "No cap",          color: "#0d9488" },   // Teal 600 (darkest)
};

/** Time phase colors (match Dashboard). */
export const EVENT_TIME_PHASE_COLORS = {
  closed: "#8CD17D",
  upcoming: "#FFBE7D",
  remaining: "#A0CBE8",
};

/** Attendance values that hold a capacity slot (excludes Waitlisted / Cancelled / No-Show). */
export const FILLED_SLOT_ATTENDANCE = new Set(["Registered", "Attended", "Walk-In"]);

/**
 * Aligns with server `buildRegistrationMetrics` + filled/attended counts.
 * Turnout denominator matches server event list `registered_total`: Registered, No-Show, Attended, Walk-In
 * (people with a registration / spot story, including no-shows). Numerator is checked-in: Attended + Walk-In.
 */
export function computeRegistrationMetrics(event, participants) {
  if (!Array.isArray(participants)) return null;
  const max =
    event && event.max_capacity != null && Number(event.max_capacity) > 0
      ? Math.floor(Number(event.max_capacity))
      : null;
  let filled = 0;
  let attended = 0;
  let waitlisted = 0;
  let turnoutRegistered = 0;
  let checkedInForTurnout = 0;
  for (const p of participants) {
    const a = (p.attendance || "").trim();
    if (a === "Waitlisted") waitlisted += 1;
    if (FILLED_SLOT_ATTENDANCE.has(a)) filled += 1;
    if (a === "Attended") attended += 1;
    if (a === "Registered" || a === "No-Show" || a === "Attended" || a === "Walk-In") turnoutRegistered += 1;
    if (a === "Attended" || a === "Walk-In") checkedInForTurnout += 1;
  }
  return {
    max_capacity: max,
    filled_count: filled,
    attended_count: attended,
    waitlisted_count: waitlisted,
    turnout_registered_count: turnoutRegistered,
    checked_in_for_turnout_count: checkedInForTurnout,
  };
}

/** Display string for Event Outcome / QR KPIs: "checked-in / registered". */
export function formatTurnoutCheckedInRegistered(metrics) {
  if (!metrics) return "—";
  const checkedIn = Number.isFinite(Number(metrics.checked_in_for_turnout_count))
    ? Number(metrics.checked_in_for_turnout_count)
    : 0;
  const registered = Number.isFinite(Number(metrics.turnout_registered_count))
    ? Number(metrics.turnout_registered_count)
    : 0;
  return `${checkedIn} / ${registered}`;
}

/** "n / d (p%)" for rates; when showPercent is false, checked-in vs no cap (no %). */
function formatCountDenominatorPercent(numer, denom, { showPercent = true } = {}) {
  const n = Number.isFinite(Number(numer)) ? Number(numer) : 0;
  if (!showPercent) {
    return `${n} / No limit`;
  }
  const d = Number.isFinite(Number(denom)) ? Number(denom) : 0;
  if (d <= 0) {
    return `${n} / ${d} (—)`;
  }
  const pct = Math.min(100, Math.round((n / d) * 100));
  return `${n} / ${d} (${pct}%)`;
}

/**
 * Card display values for Fill Rate / Turnout / Spillover (Event QR Check-In + Event Details).
 * Returns null when metrics are missing.
 */
export function buildEventRegistrationKpiDisplay(metrics) {
  if (!metrics) return null;
  const m = metrics;
  const att = Number.isFinite(Number(m.attended_count)) ? Number(m.attended_count) : 0;
  const wl = Number.isFinite(Number(m.waitlisted_count)) ? Number(m.waitlisted_count) : 0;
  const maxCap = m.max_capacity != null && Number(m.max_capacity) > 0 ? Math.floor(Number(m.max_capacity)) : null;
  const turnoutReg = Number.isFinite(Number(m.turnout_registered_count))
    ? Number(m.turnout_registered_count)
    : 0;
  const checkedInTurn = Number.isFinite(Number(m.checked_in_for_turnout_count))
    ? Number(m.checked_in_for_turnout_count)
    : 0;

  const fillFraction =
    maxCap != null ? `${att} / ${maxCap}` : formatCountDenominatorPercent(att, null, { showPercent: false });
  const fillPct = maxCap != null && maxCap > 0 ? Math.min(100, Math.round((att / maxCap) * 100)) : null;

  const turnFraction = formatTurnoutCheckedInRegistered(m);
  const turnPct = turnoutReg > 0 ? Math.min(100, Math.round((checkedInTurn / turnoutReg) * 100)) : 0;

  const spillFraction = maxCap != null ? `${wl} / ${maxCap}` : `${wl} / No limit`;
  const spillPct = maxCap != null && maxCap > 0 ? Math.min(100, Math.round((wl / maxCap) * 100)) : null;

  return {
    fillFraction,
    fillPct,
    turnFraction,
    turnPct,
    spillFraction,
    spillPct,
  };
}
